import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { SkeletonLoader } from './BasicSkeletonShapes';
import { colorBackground, colorBorder } from '../../../utils/palette';
import { useSelector } from 'react-redux';
import { Container } from 'react-bootstrap';

const MainWrapper = styled(Container)`
  display: flex;
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
  box-sizing: border-box;
  margin: 0 auto;
  max-width: ${({ screenWidth }) => (screenWidth > 2500 ? '2500px' : '100%')};
  width: 100%;
  * {
    box-sizing: border-box;
  }
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  width: 100%;
  @media (max-width: 768px) {
    flex-direction: column; // Stack vertically on small screens
  }
`;

const SidebarWrapper = styled.div`
  width: ${(props) => (props.collapse ? '50px' : '240px')};
  background: transparent;
  padding: 20px;
  border-right: 1px solid ${colorBorder};
  transition: width 0.3s;

  @media (max-width: 768px) {
    width: ${(props) => props.collapse && '50px'}; // Collapse to 50px on small screens
    // position: absolute;
    // left: 0;
    // top: 0;
    // height: 100%;
    // background-color: ${colorBackground};
    // z-index: 10;
  }

  @media (max-width: 580px) {
    width: ${(props) => props.collapse && '0px'}; // Completely hide sidebar on very small screens
    // position: absolute;
    // left: 0;
    // top: 0;
    // height: 100%;
    // background-color: ${colorBackground};
    // z-index: 10;
  }
`;

const MainContent = styled.main`
  flex: 1;
  padding: 24px;
  @media (max-width: 768px) {
    padding: 16px; // Adjust padding on small screens
  }
`;

const ProjectHeader = styled.div`
  margin-bottom: 24px;
`;

const TabsWrapper = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap; // Allow tabs to wrap on smaller screens
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 24px;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto; // Allow horizontal scrolling for small screens
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px;
  padding: 12px 24px;
  border-bottom: 1px solid ${colorBorder};
  background: ${colorBackground};
  // @media (max-width: 768px) {
  //   grid-template-columns: 1fr 1fr; // Reduce columns on small screens
  // }
`;

const TableRow = styled(TableHeader)`
  border-bottom: 1px solid ${colorBorder};
  background: ${colorBackground};
  padding: 12px 24px;
`;

export const SkeletonForRightSideContent = () => {
  const collapse = useSelector((state) => state?.sidebar?.collapse);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const calculateRightSideWidth = useCallback(() => {
    const viewportWidth = window.innerWidth;
    setScreenWidth(viewportWidth);

    if (viewportWidth <= 576) {
      return viewportWidth;
    }
    const sidebarWidth = collapse ? 60 : 240;
    const rightSideWidth = viewportWidth - sidebarWidth;
    console.log(
      'Right side content width:',
      rightSideWidth,
      'px',
      `(viewport: ${viewportWidth}px - sidebar: ${sidebarWidth}px)`,
    );
    return rightSideWidth;
  }, [collapse]);

  useEffect(() => {
    calculateRightSideWidth();
    const handleResize = () => calculateRightSideWidth();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateRightSideWidth]);

  return (
    <MainWrapper screenWidth={screenWidth}>
      <ContentWrapper>
        {screenWidth < 2500 ? (
          <>
            {!collapse ? (
              <SidebarWrapper collapse={collapse}>
                <div
                  style={{
                    padding: window.innerWidth > 580 ? '0 16px' : '0',
                    marginBottom: window.innerWidth > 580 ? '32px' : '0',
                  }}
                ></div>
              </SidebarWrapper>
            ) : (
              <div style={{ width: '50px' }}></div>
            )}
          </>
        ) : (
          <div style={{ width: '100px' }}></div>
        )}
        <MainContent>
          {/* Tabs */}
          <TabsWrapper>
            <SkeletonLoader
              isLoading={true}
              height='32px'
              width='120px'
              bgColor='rgba(50, 88, 183, 0.1)'
              waveColor='rgba(38, 37, 37, 0.3)'
            />
            <div style={{ float: 'right' }}>
              <SkeletonLoader
                isLoading={true}
                height='32px'
                width='140px'
                bgColor='rgba(50, 88, 183, 0.1)'
                waveColor='rgba(38, 37, 37, 0.3)'
              />
            </div>
          </TabsWrapper>

          {/* Action Buttons */}
          <ActionBar>
            <SkeletonLoader
              isLoading={true}
              height='36px'
              width='100px'
              bgColor='rgba(50, 88, 183, 0.1)'
              waveColor='rgba(38, 37, 37, 0.3)'
            />
            <div style={{ float: 'right' }}>
              <SkeletonLoader
                isLoading={true}
                height='36px'
                width='140px'
                bgColor='rgba(50, 88, 183, 0.1)'
                waveColor='rgba(38, 37, 37, 0.3)'
              />
            </div>
          </ActionBar>

          {/* Table */}
          <TableWrapper>
            <TableHeader collapse={collapse}>
              <SkeletonLoader
                isLoading={true}
                height='24px'
                width='120px'
                bgColor='rgba(50, 88, 183, 0.1)'
                waveColor='rgba(38, 37, 37, 0.3)'
              />
              <SkeletonLoader
                isLoading={true}
                height='24px'
                width='120px'
                bgColor='rgba(50, 88, 183, 0.1)'
                waveColor='rgba(38, 37, 37, 0.3)'
              />
              <SkeletonLoader
                isLoading={true}
                height='24px'
                width='120px'
                bgColor='rgba(50, 88, 183, 0.1)'
                waveColor='rgba(38, 37, 37, 0.3)'
              />
              <SkeletonLoader
                isLoading={true}
                height='24px'
                width='120px'
                bgColor='rgba(50, 88, 183, 0.1)'
                waveColor='rgba(38, 37, 37, 0.3)'
              />
              <SkeletonLoader
                isLoading={true}
                height='24px'
                width='120px'
                bgColor='rgba(50, 88, 183, 0.1)'
                waveColor='rgba(38, 37, 37, 0.3)'
              />
              <SkeletonLoader
                isLoading={true}
                height='24px'
                width='100px'
                bgColor='rgba(50, 88, 183, 0.1)'
                waveColor='rgba(38, 37, 37, 0.3)'
              />
            </TableHeader>

            {/* Table Rows */}
            {Array.from({ length: 4 }).map((_, rowIndex) => (
              <TableRow key={rowIndex}>
                <SkeletonLoader
                  isLoading={true}
                  height='20px'
                  width='100px'
                  bgColor='rgba(50, 88, 183, 0.1)'
                  waveColor='rgba(38, 37, 37, 0.3)'
                />
                <SkeletonLoader
                  isLoading={true}
                  height='20px'
                  bgColor='rgba(50, 88, 183, 0.1)'
                  waveColor='rgba(38, 37, 37, 0.3)'
                />
                <SkeletonLoader
                  isLoading={true}
                  height='20px'
                  width='100px'
                  bgColor='rgba(50, 88, 183, 0.1)'
                  waveColor='rgba(38, 37, 37, 0.3)'
                />
                <SkeletonLoader
                  isLoading={true}
                  height='20px'
                  width='100px'
                  bgColor='rgba(50, 88, 183, 0.1)'
                  waveColor='rgba(38, 37, 37, 0.3)'
                />
                <SkeletonLoader
                  isLoading={true}
                  height='20px'
                  width='100px'
                  bgColor='rgba(50, 88, 183, 0.1)'
                  waveColor='rgba(38, 37, 37, 0.3)'
                />
                <SkeletonLoader
                  isLoading={true}
                  height='20px'
                  width='80px'
                  bgColor='rgba(50, 88, 183, 0.1)'
                  waveColor='rgba(38, 37, 37, 0.3)'
                />
              </TableRow>
            ))}
            {/*------------------- Pagination -------------------*/}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '16px',
              }}
            >
              <SkeletonLoader
                isLoading={true}
                height='24px'
                width='120px'
                bgColor='rgba(50, 88, 183, 0.1)'
                waveColor='rgba(38, 37, 37, 0.3)'
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <SkeletonLoader
                  isLoading={true}
                  height='24px'
                  width='32px'
                  bgColor='rgba(50, 88, 183, 0.1)'
                  waveColor='rgba(38, 37, 37, 0.3)'
                />
                <SkeletonLoader
                  isLoading={true}
                  height='24px'
                  width='32px'
                  bgColor='rgba(50, 88, 183, 0.1)'
                  waveColor='rgba(38, 37, 37, 0.3)'
                />
                <SkeletonLoader
                  isLoading={true}
                  height='24px'
                  width='32px'
                  bgColor='rgba(50, 88, 183, 0.1)'
                  waveColor='rgba(38, 37, 37, 0.3)'
                />
                <SkeletonLoader
                  isLoading={true}
                  height='24px'
                  width='32px'
                  bgColor='rgba(50, 88, 183, 0.1)'
                  waveColor='rgba(38, 37, 37, 0.3)'
                />
              </div>
            </div>
          </TableWrapper>
        </MainContent>
      </ContentWrapper>
    </MainWrapper>
  );
};
